import React, { useState } from 'react';
import { Badge, Button, Drawer, Input, Modal, Space, Tag, Tooltip, Typography } from 'antd';
import { ClearOutlined, FilterOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import useIsMobile from '../../hooks/useIsMobile';

const { Text } = Typography;

/**
 * One labelled control inside the filter modal. Full width so every filter
 * lines up and stays tappable on a phone.
 */
export const FilterField = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    {label && (
      <Text strong style={{ display: 'block', marginBottom: 6 }}>
        {label}
      </Text>
    )}
    <div className="filter-field-control">{children}</div>
  </div>
);

/**
 * The standard list-page toolbar: search, refresh, Filter, and the page's
 * primary add action — the same four controls in the same order everywhere.
 *
 * List pages used to lay a search box and four or five 150-240px wide selects
 * across the top. On a phone that wrapped into four rows and pushed the table
 * off the screen; even on a laptop it ate a third of the viewport. Search stays
 * out front because it is the control people reach for constantly — everything
 * else is occasional, so it lives one tap away and gives the table its space
 * back.
 *
 * Filters apply as they are changed (the table updates live behind the modal),
 * so the footer button only dismisses. Whatever is applied also shows as a row
 * of removable chips under the bar via `activeFilters`, so what is narrowing
 * the list is visible without opening the modal and can be dropped one at a
 * time.
 *
 * Below `lg` the modal becomes a bottom sheet, which is far easier to reach
 * one-handed and is what the rest of the app already does. On a phone the add
 * button collapses to its icon — the label is the first thing worth losing when
 * the row runs out of width.
 *
 * @param {Array<{key: string, label: string, onRemove: function}>} activeFilters
 *   One entry per applied filter value. `label` should read "Field: value".
 */
const FilterBar = ({
  searchValue,
  onSearchChange,
  onSearch,
  searchPlaceholder = 'Search',
  activeCount = 0,
  activeFilters = [],
  onClear,
  title = 'Filters',
  children,
  extra,
  onRefresh,
  refreshing = false,
  addLabel,
  onAdd,
  addIcon,
  addDisabled = false,
  addClassName,
}) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile(991);
  const isPhone = useIsMobile(576);
  const hasFilters = React.Children.toArray(children).some(Boolean);
  // Fall back to the chip count so a page that only passes chips still badges.
  const appliedCount = activeCount || activeFilters.length;

  // On a phone every control in the row is icon-only; the labels are the
  // first thing worth dropping when the width runs out.
  const filterButton = (
    <Badge count={appliedCount} size="small" offset={[-2, 2]}>
      <Tooltip title={isPhone ? 'Filter' : undefined}>
        <Button
          icon={<FilterOutlined />}
          onClick={() => setOpen(true)}
          type={appliedCount > 0 ? 'primary' : 'default'}
          aria-label="Filter"
        >
          {isPhone ? null : 'Filter'}
        </Button>
      </Tooltip>
    </Badge>
  );

  const refreshButton = onRefresh && (
    <Tooltip title="Refresh">
      <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={refreshing} />
    </Tooltip>
  );

  const addButton = onAdd && (
    <Tooltip title={isPhone ? addLabel : undefined}>
      <Button
        type="primary"
        // A bare "+" on a phone: it is the one control whose meaning survives
        // without a label, and a page-specific glyph would not.
        icon={isPhone ? <PlusOutlined /> : addIcon || <PlusOutlined />}
        onClick={onAdd}
        disabled={addDisabled}
        className={addClassName}
        aria-label={addLabel}
      >
        {isPhone ? null : addLabel}
      </Button>
    </Tooltip>
  );

  const footer = (
    <div style={{ display: 'flex', gap: 12 }}>
      {onClear && (
        <Button
          icon={<ClearOutlined />}
          onClick={onClear}
          disabled={appliedCount === 0}
          style={{ flex: 1 }}
        >
          Clear All
        </Button>
      )}
      <Button type="primary" onClick={() => setOpen(false)} style={{ flex: 1 }}>
        Done
      </Button>
    </div>
  );

  const heading = (
    <Space>
      <FilterOutlined />
      <span>{title}</span>
    </Space>
  );

  return (
    <>
      <div className="filter-bar">
        <Input.Search
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onSearch={onSearch}
          allowClear
          className="filter-bar-search"
        />
        <Space size={8} className="filter-bar-actions">
          {extra}
          {refreshButton}
          {hasFilters && filterButton}
          {addButton}
        </Space>
      </div>

      {activeFilters.length > 0 && (
        <div className="filter-bar-tags">
          {activeFilters.map((filter) => (
            <Tag
              key={filter.key}
              closable
              onClose={(event) => {
                event.preventDefault();
                filter.onRemove();
              }}
              color="blue"
            >
              {filter.label}
            </Tag>
          ))}
          {onClear && activeFilters.length > 1 && (
            <a onClick={onClear} className="filter-bar-clear">
              Clear all
            </a>
          )}
        </div>
      )}

      {hasFilters &&
        (isMobile ? (
          <Drawer
            title={heading}
            placement="bottom"
            height="85vh"
            open={open}
            onClose={() => setOpen(false)}
            footer={footer}
            styles={{ body: { paddingBottom: 24 } }}
            className="filter-panel"
          >
            {children}
          </Drawer>
        ) : (
          <Modal
            title={heading}
            open={open}
            onCancel={() => setOpen(false)}
            footer={footer}
            width={520}
            className="filter-panel"
          >
            {children}
          </Modal>
        ))}
    </>
  );
};

export default FilterBar;
